import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { referralAPI } from '../services/api';
import { toast } from 'react-hot-toast';
import {
  LinkIcon,
  ClipboardIcon,
  ShareIcon,
  ChartBarIcon,
  UserGroupIcon,
  GiftIcon,
  CheckCircleIcon,
  ClockIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import {
  FacebookIcon,
  TwitterIcon,
  EmailIcon,
} from 'react-share';

const Referrals = () => {
  const [copied, setCopied] = useState(false);

  // Fetch referral link
  const { data: referralLinkData, isLoading: linkLoading } = useQuery({
    queryKey: ['referral-link'],
    queryFn: () => referralAPI.getMyReferralLink().then(res => res.data),
    retry: 2,
  });

  // Fetch referral stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['referral-stats'],
    queryFn: () => referralAPI.getMyReferralStats().then(res => res.data),
    retry: 2,
  });

  // Fetch recent referrals
  const { data: recentReferrals, isLoading: recentLoading } = useQuery({
    queryKey: ['recent-referrals'],
    queryFn: () => referralAPI.getRecentReferrals(10).then(res => res.data),
    retry: 2,
  });

  const referralLink = referralLinkData?.referral_link || '';
  const referralCode = referralLinkData?.referral_code || '';

  const copyToClipboard = async () => {
    if (referralLink) {
      try {
        await navigator.clipboard.writeText(referralLink);
        setCopied(true);
        toast.success('Referral link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast.error('Failed to copy link');
      }
    }
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent('Join me on Vaulto!');
    const body = encodeURIComponent(
      `Hey! I've been using Vaulto to backup my Instagram posts and I think you'd love it too. Sign up using my referral link and we both get 1 month free Premium when you subscribe!\n\n${referralLink}`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const shareViaTwitter = () => {
    const text = encodeURIComponent(
      `Join me on Vaulto! Backup your Instagram posts and get 1 month free Premium when you subscribe. Use my link: ${referralLink}`
    );
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
  };

  const shareViaFacebook = () => {
    const url = encodeURIComponent(referralLink);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(
      `Join me on Vaulto! Backup your Instagram posts and get 1 month free Premium when you subscribe. Use my link: ${referralLink}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  if (linkLoading || statsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center mb-4">
          <div className="h-20 w-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl flex items-center justify-center shadow-2xl transform hover:scale-105 transition-all duration-300">
            <GiftIcon className="h-10 w-10 text-white" />
          </div>
        </div>
        <h1 className="text-5xl font-extrabold text-white mb-4 bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent">
          Referral Program
        </h1>
        <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
          Share Vaulto with friends and get rewarded! When someone signs up using your link and subscribes to Premium or Pro, you both get{' '}
          <span className="text-pink-300 font-bold bg-white/10 px-2 py-1 rounded-lg">1 month free Premium</span>.
        </p>
      </div>

      {/* Referral Link Card */}
      <div className="bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-purple-500/10 rounded-3xl p-8 border border-white/10 backdrop-blur-sm hover:border-white/20 transition-all duration-300">
        <div className="flex items-center mb-6">
          <div className="h-14 w-14 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-xl mr-4 transform rotate-3">
            <LinkIcon className="h-7 w-7 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Your Referral Link</h2>
            <p className="text-purple-200">Share this link with friends to start earning rewards</p>
          </div>
        </div>
        
        <div className="bg-black/20 rounded-2xl p-5 backdrop-blur-sm">
          <div className="flex items-center space-x-4">
            <input
              type="text"
              readOnly
              value={referralLink}
              className="flex-1 px-5 py-4 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent font-mono text-base hover:bg-white/10 transition-all duration-200"
            />
            <button
              onClick={copyToClipboard}
              className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 flex items-center space-x-3 ${
                copied
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-2xl shadow-green-500/30'
                  : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-2xl shadow-purple-500/30 hover:shadow-purple-500/50'
              }`}
            >
              {copied ? (
                <>
                  <CheckCircleIcon className="h-6 w-6" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <ClipboardIcon className="h-6 w-6" />
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>
          <p className="text-base text-purple-200 mt-4 flex items-center">
            <SparklesIcon className="h-5 w-5 mr-2 text-pink-400" />
            Your referral code: <span className="font-mono font-bold text-pink-300 ml-2 text-lg bg-white/10 px-3 py-1 rounded-lg">{referralCode}</span>
          </p>
        </div>
      </div>

      {/* Share Buttons */}
      <div className="bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-indigo-500/10 rounded-3xl p-8 border border-white/10 backdrop-blur-sm hover:border-white/20 transition-all duration-300">
        <div className="flex items-center mb-6">
          <div className="h-14 w-14 bg-gradient-to-br from-pink-500 to-rose-500 rounded-2xl flex items-center justify-center shadow-xl mr-4 transform -rotate-3">
            <ShareIcon className="h-7 w-7 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Share Your Link</h2>
            <p className="text-purple-200">Spread the word on your favorite platforms</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={shareViaEmail}
            className="group relative bg-gradient-to-br from-gray-600/20 to-gray-700/20 hover:from-gray-600/30 hover:to-gray-700/30 rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 transform hover:scale-105 hover:shadow-xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex flex-col items-center">
              <div className="mb-3 transform group-hover:scale-110 transition-transform duration-300">
                <EmailIcon size={48} round />
              </div>
              <span className="text-base font-semibold text-white">Email</span>
            </div>
          </button>
          
          <button
            onClick={shareViaTwitter}
            className="group relative bg-gradient-to-br from-sky-500/20 to-blue-600/20 hover:from-sky-500/30 hover:to-blue-600/30 rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 transform hover:scale-105 hover:shadow-xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex flex-col items-center">
              <div className="mb-3 transform group-hover:scale-110 transition-transform duration-300">
                <TwitterIcon size={48} round />
              </div>
              <span className="text-base font-semibold text-white">Twitter</span>
            </div>
          </button>
          
          <button
            onClick={shareViaFacebook}
            className="group relative bg-gradient-to-br from-blue-600/20 to-indigo-600/20 hover:from-blue-600/30 hover:to-indigo-600/30 rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 transform hover:scale-105 hover:shadow-xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex flex-col items-center">
              <div className="mb-3 transform group-hover:scale-110 transition-transform duration-300">
                <FacebookIcon size={48} round />
              </div>
              <span className="text-base font-semibold text-white">Facebook</span>
            </div>
          </button>
          
          <button
            onClick={shareViaWhatsApp}
            className="group relative bg-gradient-to-br from-green-500/20 to-emerald-600/20 hover:from-green-500/30 hover:to-emerald-600/30 rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 transform hover:scale-105 hover:shadow-xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex flex-col items-center">
              <div className="mb-3 transform group-hover:scale-110 transition-transform duration-300">
                <svg className="w-12 h-12" viewBox="0 0 24 24" fill="#25D366">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .96 4.534.96 10.09c0 1.792.413 3.476 1.142 4.996L0 24l8.937-2.089a11.9 11.9 0 003.113.414h.004c5.554 0 10.09-4.535 10.09-10.09 0-2.736-1.095-5.219-2.87-7.03"/>
                </svg>
              </div>
              <span className="text-base font-semibold text-white">WhatsApp</span>
            </div>
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="group bg-gradient-to-br from-blue-500/10 to-indigo-500/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105 hover:shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <div className="h-14 w-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-xl group-hover:shadow-cyan-500/30 transition-all duration-300">
              <UserGroupIcon className="h-7 w-7 text-white" />
            </div>
          </div>
          <p className="text-purple-200 mb-2">Total Referrals</p>
          <p className="text-4xl font-bold text-white">
            {stats?.total_referrals || 0}
          </p>
        </div>

        <div className="group bg-gradient-to-br from-green-500/10 to-emerald-500/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105 hover:shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <div className="h-14 w-14 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-xl group-hover:shadow-emerald-500/30 transition-all duration-300">
              <CheckCircleIcon className="h-7 w-7 text-white" />
            </div>
          </div>
          <p className="text-purple-200 mb-2">Converted</p>
          <p className="text-4xl font-bold text-white">
            {stats?.converted_referrals || 0}
          </p>
        </div>

        <div className="group bg-gradient-to-br from-yellow-500/10 to-orange-500/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105 hover:shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <div className="h-14 w-14 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-xl group-hover:shadow-orange-500/30 transition-all duration-300">
              <GiftIcon className="h-7 w-7 text-white" />
            </div>
          </div>
          <p className="text-purple-200 mb-2">Rewarded</p>
          <p className="text-4xl font-bold text-white">
            {stats?.rewarded_referrals || 0}
          </p>
        </div>

        <div className="group bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105 hover:shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <div className="h-14 w-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-xl group-hover:shadow-pink-500/30 transition-all duration-300">
              <ChartBarIcon className="h-7 w-7 text-white" />
            </div>
          </div>
          <p className="text-purple-200 mb-2">Conversion Rate</p>
          <p className="text-4xl font-bold text-white">
            {stats?.conversion_rate ? `${stats.conversion_rate.toFixed(1)}%` : '0%'}
          </p>
        </div>
      </div>

      {/* Recent Referrals */}
      <div className="bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10 rounded-3xl p-8 border border-white/10 backdrop-blur-sm hover:border-white/20 transition-all duration-300">
        <div className="flex items-center mb-6">
          <div className="h-14 w-14 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-xl mr-4 transform rotate-6">
            <SparklesIcon className="h-7 w-7 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Recent Referrals</h2>
            <p className="text-purple-200">Track your referral activity</p>
          </div>
        </div>
        
        {recentLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        ) : recentReferrals && recentReferrals.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-purple-200 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-purple-200 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-purple-200 uppercase tracking-wider">
                    Subscription Tier
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {recentReferrals.map((referral) => (
                  <tr key={referral.id} className="hover:bg-white/5 transition-colors duration-200">
                    <td className="px-6 py-5 whitespace-nowrap">
                      <span
                        className={`px-4 py-2 inline-flex text-sm font-bold rounded-xl shadow-lg ${
                          referral.status === 'rewarded'
                            ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-300 border border-green-500/30'
                            : referral.status === 'converted'
                            ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-300 border border-blue-500/30'
                            : 'bg-gradient-to-r from-gray-500/20 to-slate-500/20 text-gray-300 border border-gray-500/30'
                        }`}
                      >
                        {referral.status.charAt(0).toUpperCase() + referral.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-base text-gray-300">
                      {new Date(referral.created_at).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      {referral.referee_subscription_tier ? (
                        <span className="px-3 py-1.5 bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 rounded-lg text-sm font-semibold border border-purple-500/30">
                          {referral.referee_subscription_tier.charAt(0).toUpperCase() + referral.referee_subscription_tier.slice(1)}
                        </span>
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full mb-6 animate-pulse">
              <ClockIcon className="h-12 w-12 text-purple-300" />
            </div>
            <p className="text-gray-300 text-xl mb-3">No referrals yet</p>
            <p className="text-gray-400 text-base max-w-md mx-auto">
              Start sharing your referral link to see your referrals appear here. Each successful referral brings you closer to free Premium time!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Referrals;